import React from 'react';
import { ReceiptDoc, CompanyProfileDoc } from './types';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });

const numberToWords = (num: number): string => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

  if (num === 0) return 'Zero';

  function convertHundreds(n: number): string {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n >= 10) {
      result += teens[n - 10] + ' ';
      return result.trim();
    }
    if (n > 0) {
      result += ones[n] + ' ';
    }
    return result.trim();
  }

  let result = '';
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = num % 1000;

  if (crore > 0) result += convertHundreds(crore) + ' Crore ';
  if (lakh > 0) result += convertHundreds(lakh) + ' Lakh ';
  if (thousand > 0) result += convertHundreds(thousand) + ' Thousand ';
  if (hundred > 0) result += convertHundreds(hundred) + ' ';

  return result.trim() + ' Rupees Only';
};

interface Props {
  receipt: ReceiptDoc;
  company: CompanyProfileDoc | null;
}

// Read-only presentational receipt (mirrors ReceiptDetails' #receipt-content,
// minus the TDS inline-edit affordance). Used by the public share page.
const ReceiptPrintable: React.FC<Props> = ({ receipt, company }) => {
  const cgst = receipt.cgstAmount ?? 0;
  const sgst = receipt.sgstAmount ?? 0;
  const igst = receipt.igstAmount ?? 0;
  const hasTaxAmount = cgst > 0 || sgst > 0 || igst > 0;

  let subtotalValue = 0;
  if (receipt.subtotal !== undefined && receipt.subtotal !== null) {
    if (typeof receipt.subtotal === 'number') {
      subtotalValue = receipt.subtotal;
    } else {
      const parsed = parseFloat(String(receipt.subtotal));
      subtotalValue = isNaN(parsed) ? 0 : parsed;
    }
  }
  const hasTaxInfo = hasTaxAmount && subtotalValue > 0 && receipt.amount > 0;
  const tdsValue = receipt.tdsDeducted ?? 0;
  const hasTDS = tdsValue > 0;

  return (
    <div
      id="receipt-content"
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
    >
      {/* Company Logo and Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 items-start">
        <div>
          {company?.logo?.url && (
            <img src={company.logo.url} alt="Company Logo" className="h-16 mb-4 object-contain" />
          )}
          <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-12">From:</h3>
          <div className="space-y-1">
            <p className="font-medium text-gray-900">{company?.companyName}</p>
            <p className="text-gray-600">{company?.email}</p>
            {company?.contactNumber && <p className="text-gray-600">{company?.contactNumber}</p>}
            {company && (
              <div className="text-gray-600">
                <p>{company.address.street}</p>
                <p>{company.address.city}, {company.address.state} - {company.address.pincode}</p>
                <p>{company.address.country}</p>
              </div>
            )}
            <p className="text-gray-600">GSTIN: {company?.gstNumber}</p>
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">PAYMENT RECEIPT</h2>
          <div className="space-y-1 mb-6">
            <p className="text-gray-600">Receipt No: {receipt.receiptNumber}</p>
            <p className="text-gray-600">Date: {formatDate(receipt.receiptDate)}</p>
          </div>
          <div className="pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600 mb-2">
              {hasTDS ? 'Net Amount Received' : 'Amount Received'}
            </div>
            <div className="text-2xl font-bold text-primary-600">
              {formatCurrency(Math.round(hasTDS ? receipt.amount - tdsValue : receipt.amount))}
            </div>
          </div>
        </div>
      </div>

      {/* Client Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Received From:</h3>
          <div className="space-y-1">
            <p className="font-medium text-gray-900">{receipt.client.clientName}</p>
            <p className="text-gray-600">{receipt.client.email}</p>
            {receipt.client.contactNumber && <p className="text-gray-600">{receipt.client.contactNumber}</p>}
            <div className="text-gray-600">
              <p>{receipt.client.address.street}</p>
              <p>{receipt.client.address.city}, {receipt.client.address.state} - {receipt.client.address.pincode}</p>
              <p>{receipt.client.address.country}</p>
            </div>
            {receipt.client.gstin && (
              <p className="text-gray-600">GSTIN: {receipt.client.gstin}</p>
            )}
          </div>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Details:</h3>
          <div className="space-y-1 text-gray-600">
            <p><strong>Invoice Number:</strong> {receipt.invoiceNumber}</p>
            <p><strong>Payment Mode:</strong> {receipt.paymentMode}</p>
            {receipt.paymentDetails?.referenceNumber && (
              <p><strong>Reference Number:</strong> {receipt.paymentDetails.referenceNumber}</p>
            )}
            {receipt.paymentDetails?.transactionDate && (
              <p><strong>Transaction Date:</strong> {formatDate(receipt.paymentDetails.transactionDate)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Receipt Items - Show if items exist */}
      {receipt.items && receipt.items.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Goods/Services Description:</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SAC</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {receipt.items.map((item, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.serialNumber}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{item.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.sac || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.rate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Summary (read-only) */}
      <div className="mb-8 p-4 bg-primary-50 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Payment Summary</h3>
        </div>
        <div className="space-y-4">
          {hasTaxInfo && (
            <>
              {subtotalValue > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Subtotal:</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(subtotalValue)}</span>
                </div>
              )}
              {cgst > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">CGST ({receipt.cgstRate || 0}%):</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(cgst)}</span>
                </div>
              )}
              {sgst > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">SGST ({receipt.sgstRate || 0}%):</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(sgst)}</span>
                </div>
              )}
              {igst > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">IGST ({receipt.igstRate || 0}%):</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(igst)}</span>
                </div>
              )}
              {hasTDS && receipt.amount > 0 && (
                <div className="flex justify-between items-center border-t border-gray-300 pt-3 mb-2">
                  <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-xl font-bold text-gray-900">{formatCurrency(receipt.amount)}</span>
                </div>
              )}
            </>
          )}

          {hasTDS ? (
            <div className="flex justify-between items-center border-t border-primary-200 pt-4">
              <span className="text-sm font-semibold text-gray-700">
                TDS Deducted ({receipt.tdsPercentage || 0}%):
              </span>
              <span className="text-base font-medium text-gray-900">
                -{formatCurrency(tdsValue)}
              </span>
            </div>
          ) : null}

          <div className="flex justify-between items-center border-t border-primary-300 pt-4">
            <span className="text-lg font-semibold text-gray-900">
              {hasTDS ? 'Net Amount Received:' : hasTaxInfo ? 'Total Amount:' : 'Amount Received:'}
            </span>
            <span className="text-2xl font-bold text-primary-600">
              {formatCurrency(Math.round(hasTDS ? receipt.amount - tdsValue : receipt.amount))}
            </span>
          </div>

          <div className="border-t border-primary-200 pt-4">
            <p className="text-sm text-gray-600">
              <strong>Amount in Words:</strong> {
                hasTDS
                  ? numberToWords(Math.round(receipt.amount - tdsValue)) + ' Only'
                  : receipt.amountInWords
              }
            </p>
          </div>
        </div>
      </div>

      {/* Notes */}
      {receipt.notes && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes:</h3>
          <p className="text-gray-600">{receipt.notes}</p>
        </div>
      )}

      {/* Terms and Conditions */}
      {company?.receiptTermsAndConditions && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Terms & Conditions:</h3>
          <p className="text-sm text-gray-600">{company.receiptTermsAndConditions}</p>
        </div>
      )}

      {/* Signature */}
      <div className="mt-8 flex justify-end">
        <div className="text-center">
          {company?.digitalSignature?.url && (
            <div className="mb-4">
              <img src={company.digitalSignature.url} alt="Digital Signature" className="h-16 object-contain mx-auto" />
            </div>
          )}
          <p className="text-sm font-semibold text-gray-900">{company?.companyName || ''}</p>
          <p className="text-xs text-gray-500 mt-1">Authorized Signatory</p>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPrintable;
